import React from 'react';
import { getChannelUnreadMessageCount } from 'shared/hooks/useUnreadMessageCount';
import { useClearUnreadMessagesMutation } from 'shared/types/graphql';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';

interface MarkAllAsReadConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MarkAllAsReadConfirmationModal = ({ isOpen, onClose }: MarkAllAsReadConfirmationModalProps): JSX.Element => {
  const { clinicUser, currentClinicId } = useClinicUser();
  const toast = useToast();

  const [clearUnreadMessages, { loading }] = useClearUnreadMessagesMutation({
    refetchQueries: [getChannelUnreadMessageCount],
  });

  const handleConfirmationClick = async (): Promise<void> => {
    const userId = clinicUser?.id;

    if (!(userId && currentClinicId)) return;
    try {
      await clearUnreadMessages({ variables: { userId, clinicId: currentClinicId } });
      toast({
        status: 'success',
        title: 'Success! All conversations marked as "read"',
      });
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: 'Whoops! There was an error marking all conversations as "read." Please try again.',
      });
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalHeader>Mark All Conversations as Read</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text>
          This will apply to all conversations, regardless of your current filter selection or notification settings.
          Are you sure you want to proceed?
        </Text>
      </ModalBody>
      <ModalFooterButtons onSubmit={handleConfirmationClick} submitText="Mark All as Read" isLoading={loading} />
    </Modal>
  );
};

export default MarkAllAsReadConfirmationModal;
